import styled, { keyframes, css } from "styled-components";
import {
  colors,
  spacings,
  borders,
  fontSizes,
  fontWeights,
  shadows
} from 'styles/theme'

type SizeBtnProps = "small" | "large";
type AppearWhereProps = "bottom" | "top";

interface SelectProps {
  block: boolean;
}

interface SelectHeaderProps {
  block: boolean;
  size: SizeBtnProps;
  readOnly: boolean;
  showDropdown: boolean;
  touched: boolean;
  appearWhere?: AppearWhereProps;
  error?: boolean;
  selected: boolean;
}

interface SelectOptionsContainerProps {
  selected: boolean,
  itemSelected: boolean,  
  minWidthOptions?: number,
  appearWhere?: AppearWhereProps,
}

const sizes = {
  small: "40px",
  large: "53px"
}

const fade = keyframes`
  from {
    opacity: 0;
  },
  to{
    opacity: 1;
  }
`

export const Select = styled.div<SelectProps>`
  position: relative;
  display: ${props => props.block ? "block" : "inline-flex"};
  width: 100%;
  margin-right: ${spacings.md};
  cursor: pointer;

  &:last-child{
    margin-right: 0;
  };
`

export const SelectHeader = styled.div<SelectHeaderProps>`
  width: 100%;
  max-width: ${props => props.block ? "100%" : "330px"};
  min-height: ${props => sizes[props.size]};
  height: 100%;
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  max-width: auto;
  min-height: ${props => sizes[props.size]};
  padding: 0 ${props =>  props.size === "small" ? `${spacings.sm}` : `${spacings.md}`};
  overflow: hidden;
  text-overflow: ellipsis;
  /* z-index: 0;  FICOU COMO 0 POIS NAS TELAS ONDE EXISTE UM DATEPICKER ACIMA, O SELECT SOBREPOE O CALENDAR DO DATEPICKER - REMOVER COMENTÁRIO AO SOLUCIONAR */

  border-radius: ${props => props.size === "small" ? `${borders.md}` : `${borders.lg}`};
  border: 2px solid ${colors.neutral_medium};
  ${({selected}) => selected && css`border: 2px solid ${colors.primary}`};
  ${({readOnly}) => readOnly && css`border: 2px solid ${colors.neutral_medium}`};
  ${({error}) => error && css`border: 2px solid ${colors.secondary_four}`};

  .select {
    pointer-events: none;
    transition: all 0.2s linear;
    flex: 1;
    display: flex;
    align-items: center;
    min-height: 100%;

    strong{
      font-family: DM Sans;
      font-style: normal;
      font-weight: ${fontWeights.w700};
      font-size: ${fontSizes.sm};
      color: ${props => props.readOnly ? colors.neutral_darker : colors.neutral_darkest};
      text-overflow: ellipsis;
    }
  }

  button{
    display: flex;
    justify-content: center;
    align-items: center;
    width: 24px;
    min-width: 24px;
    height: 24px;
    margin-left: ${spacings.sl};
    background: ${colors.neutral_medium};
    border-radius: ${borders.sm};
    border: none;
    ${(props) => props.readOnly && css`pointer-events: none;`}
    
    svg {
      transform: ${(props) => props.appearWhere === "bottom" ? "rotate(180deg)" : "rotate(0deg)"}
    }

    svg {
      transition: all 0.2s linear;
      ${(props) => props.showDropdown && css`
        animation: arrowEffectButton 5s linear infinite;
        transform: ${props.appearWhere === "bottom" ? "rotate(0deg)" : "rotate(180deg)"}
      `};
    }
      
    &:hover {
      filter: brightness(0.9);
    }
  }
`

export const SelectOptionsContainer = styled.div<SelectOptionsContainerProps>`
  z-index: 10;
  ${props => props.selected ? "display: block;" : "display: none;"}
  position: absolute;
  ${props => props.appearWhere === "top" ? "bottom: 45px;" : "top: 45px;"}
  left: 0px;
  width: 100%;
  min-width: ${props => props.minWidthOptions}%;
  width: auto;
  height: auto;
  max-height: 200px;
  border-radius: ${borders.lg};
  background: ${colors.white};
  overflow: auto;
  box-shadow: ${shadows.sm};
  animation: ${fade} 0.2s linear;
  margin-top: 0.75rem;
  margin-bottom: 0.75rem;
`

export const SelectOptionsList = styled.div`
  display: flex;
  flex-direction: column;  
`

export const Option = styled.div`
  display: flex;
  position: relative;
  align-items: center;
  padding: ${spacings.sm};
  width: 100%;

  &.isActive{
    span{
      background: ${colors.primary};
    }
  }

  &.readOnly{
    background: red;
  }

  span{
    display: inline-block;
    position: absolute;
    height: 15px;
    width: 15px;
    margin-right: ${spacings.sm};
    border-radius: 50%;
    border: ${borders.xs} solid ${colors.primary};
    pointer-events: none;
  }
  
  strong{
    font-family: DM Sans;
    font-weight: ${fontWeights.w500};
    font-size: ${fontSizes.sm};
    color: ${colors.neutral_darkest};
    pointer-events: none;
    padding-left: 1.75rem;
  }

  &:hover{
    background-color: ${colors.white};
  }
`

export const SelectedTagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacings.xs};
  align-items: center;
  min-height: 100%;
  padding: ${spacings.xs} 0;
  pointer-events: none;
`

export const Tag = styled.div`
  display: flex;
  align-items: center;
  background: ${colors.support_one_light};
  border-radius: ${borders.sm};
  padding: ${spacings.xs} ${spacings.sm};
  gap: ${spacings.xs};
  max-width: 200px;
  
  span {
    font-family: DM Sans;
    font-weight: ${fontWeights.w500};
    font-size: ${fontSizes.xs};
    color: ${colors.primary};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`

export const TagRemoveButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  cursor: pointer;
  padding: 2px;
  border-radius: 50%;
  width: 16px;
  height: 16px;
  min-width: 16px;
  pointer-events: auto;
  
  svg {
    width: 12px;
    height: 12px;
    color: ${colors.neutral_darker};
  }
  
  &:hover {
    background: ${colors.neutral_medium};
    svg {
      color: ${colors.neutral_darkest};
    }
  }
`

export const ErrorSpan = styled.span`
  position: absolute;
  bottom: -15px;
  left: 0;
  width: 100%;
  padding-top: ${spacings.xs};
  padding-left: ${spacings.md};
  color: ${colors.secondary_four};
  font-weight: ${fontWeights.w700};
  font-size: ${fontSizes.xs};
`

export const HiddenSelect = styled.select`
  display: none;
`

export const Label = styled.label`
  font-family: DM Sans;
  font-style: normal;
  font-weight: ${fontWeights.w700};
  font-size: ${fontSizes.sm};
  color: ${colors.neutral_darkest};
  text-overflow: ellipsis;
`